import { type LocationQuery } from 'vue-router';
import HotelRoomType from '../itinerary/hotel/HotelRoomType';
import RoomsPackage from '../itinerary/hotel/RoomsPackage';
import HotelPackage from '@itinerary/hotel/HotelPackage';
import HotelPriceClass from '@itinerary/hotel/HotelPriceClass';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import { type TripShareInfo as TrTripShareInfo } from '@itinerary/trip/TripModuleTypes';
import { type Location, type ReserveInfo } from '@utils/types';
import type ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import type PriceClass from '@itinerary/PriceClass';
import BusPackage from '@itinerary/trip/bus/BusPackage';
import BusPriceClass from '@itinerary/trip/bus/BusPriceClass';
import FlightPackage from '@itinerary/trip/flight/FlightPackage';
import FlightAutoPriceClass from '@itinerary/trip/flight/FlightAutoPriceClass';
import FlightPriceClass from '@itinerary/trip/flight/FlightPriceClass';
import { FlightSegment } from '@itinerary/trip/flight/FlightSegment';
import TrainPackage from '@itinerary/trip/train/TrainPackage';
import TrainPriceClass from '@itinerary/trip/train/TrainPriceClass';
import TripSegment from '@itinerary/trip/TripSegment';
import {
    ServerDisconnectToDateException,
    TrainNoServerResponseError,
} from '@itinerary/Exceptions';
import {
    type EmptyListReason,
    type Filter,
    type Filters,
    type ListErrorMV,
    LoadingStatus,
    NetworkError,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import {
    AutoReserveType,
    type ItineraryParams,
    ReserveType,
} from '@utils/itinerary';
import BusAutoPriceClass from '@itinerary/trip/bus/BusAutoPriceClass';
import { type ItineraryPackageLoaderConfig } from '@itinerary/types';

export default abstract class ItineraryModule<
    TPrice extends PriceClass = PriceClass,
    TFilters extends Filters = Filters,
    TLocation extends Location<TLocation> = Location,
    TPackage extends ItineraryPackage<TPrice> = ItineraryPackage<TPrice>,
> {
    //#region state

    get hasPreSell() {
        return this.currentPackageSet?.metaData?.hasPreSell ?? false;
    }

    get routeNotFound() {
        return this.currentPackageSet?.metaData?.routeNotFound ?? false;
    }

    get priceTrackEnabled() {
        return this.currentPackageSet?.metaData?.priceTrackEnabled ?? true;
    }

    lateralPackage: BusPackage | null = null;

    abstract get metaTagTitle(): string;

    abstract itineraryPackageSets: (ItineraryPackageSet<
        TFilters['meta'],
        TFilters['key'],
        TPackage
    > | null)[];

    abstract sortBy: string;

    isInitialSort = true;

    listErrorReason: ListErrorMV | null = null;

    isCouponAppliedInList = false;

    //#endregion

    get defaultAutoReserveType(): AutoReserveType {
        return AutoReserveType.None;
    }

    get showSearchSuggestion(): boolean {
        return !this.routeNotFound;
    }

    protected abstract get reserveInfo(): ReserveInfo;

    get unknownListError(): ListErrorMV {
        return {
            id: 'connection-lost',
            type: 'Unknown',
            image: '/img/error/connectionLost.svg',
            message: 'متاسفیم!',
            description: `در حال حاضر سرور ${this.reserveInfo.title} قطع می‌باشد!`,
        };
    }

    /**
     * return a error text for empty list
     *
     * @readonly
     * @abstract
     * @type {EmptyListReason}
     * @memberof ItineraryModule
     */
    abstract get emptyListReason(): EmptyListReason;
    get isSelectingReturn(): boolean {
        return useItineraryStore().isSelectingReturn;
    }

    /**
     * GETTER - Loads suggestion for the related vehicle, using the provided route info
     *
     * @param {any} params
     * @returns {void}
     * @memberof ItineraryModule
     */
    abstract loadSuggestions(params: TrTripShareInfo): void;

    terminatePackageReserve(_reserveId: number): void | Promise<void> {
        // empty body for parent classes
        // may be overriden by child classes if needed.
    }

    setPackageSet(params: {
        index: number;
        set: ItineraryPackageSet<
            TFilters['meta'],
            TFilters['key'],
            TPackage
        > | null;
        totalLength?: number;
    }) {
        this.itineraryPackageSets[params.index] = params.set;

        if (params.totalLength) {
            this.itineraryPackageSets.length = params.totalLength;
        }
    }

    /**
     * reset last load time of loaders
     *
     * @memberof ItineraryModule
     */
    resetLoaders() {
        this.itineraryPackageSets.forEach(ps => ps?.resetLastLoad());
    }

    /**
     * get sort by list of current itinerary module
     *
     * @readonly
     * @abstract
     * @type {SortBy[]}
     * @memberof ItineraryModule
     */
    abstract get sortByList(): SortBy[];

    /**
     * Sets the values of applied filters
     */
    setFilterValues(
        filters: { [key in TFilters['key']]: any } | null | undefined
    ) {
        const currentIndex = useItineraryStore().currentSetIndex;
        if (!this.itineraryPackageSets[currentIndex] || !filters) {
            return;
        }
        this.itineraryPackageSets[currentIndex].filters = { ...filters };
    }

    get filterValues(): { [key in Filters['key']]: any } | null | undefined {
        const currentIndex = useItineraryStore().currentSetIndex;
        return this.itineraryPackageSets[currentIndex]?.filters;
    }

    /**
     * When overridden, specifies the default search method for the reserve
     *
     * @readonly
     * @abstract
     * @type {string}
     * @memberof ItineraryModule
     */
    abstract get defaultSortBy(): string;

    abstract applySort(list: ItineraryPackage[]): ItineraryPackage[];

    /**
     * get sorted list base on sortBy
     *
     * @readonly
     * @abstract
     * @memberof ItineraryModule
     */
    abstract get sortedReservableList(): ItineraryPackage[];

    abstract get sortedNonReservableList(): ItineraryPackage[];

    get hasAutoReserve(): boolean {
        return this.defaultAutoReserveType !== AutoReserveType.None;
    }

    get autoReserveOnly(): boolean {
        return this.hasAutoReserve && !this.sortedReservableList.length;
    }

    get autoReservePriceOnly(): boolean {
        if (this.hasAutoReserve) {
            return (
                this.autoReservableList.length === 0 &&
                !!this.sortedReservableList.length
            );
        }
        return false;
    }

    abstract get emptyPackage(): ItineraryPackage;

    get autoReservableList(): ItineraryPackage[] {
        return this.applySort(this.list.filter(pkg => pkg.autoReserveEnabled));
    }

    get extraAutoReserveItem(): ItineraryPackage | null {
        return null;
    }

    /**
     * handle change of first date (like departure date in trip)
     *
     * @abstract
     * @param {Date} date
     * @memberof ItineraryModule
     */
    abstract handleFirstDateChange(date: Date | null): void;
    /**
     * GETTER - Returns the description for the view page
     *
     * @returns {string}
     * @memberof ItineraryModule
     */
    abstract get description(): string | null;

    /**
     * GETTER - When overridden, specified the description template that must be used in view page, for SEO's sake
     *
     * @returns {string}
     * @memberof ItineraryModule
     */
    abstract get descriptionTemplate(): string;

    /**
     * GETTER - When overridden, use to generate canonical head
     *
     * @readonly
     * @abstract
     * @type {RegExp}
     * @memberof ItineraryModule
     */
    abstract get canonicalRegex(): RegExp;

    /**
     * Clears the loaded information from availability service
     *
     * @memberof ItineraryModule
     */
    clear() {
        this.itineraryPackageSets.forEach(set => set?.stop());
        this.itineraryPackageSets = [];
    }

    /**
     * UTILITY - When overridden, returns an initial instance of filter object
     */
    abstract getInitialFilters(): { [key in TFilters['key']]: any };

    /**
     * get new package set base on loader
     *
     * @abstract
     * @param {ItineraryPackageLoader} loaderConfig
     * @returns {ItineraryPackageSet}
     * @memberof ItineraryModule
     */
    abstract getPackageSet(
        loaderConfig: ItineraryPackageLoaderConfig
    ): ItineraryPackageSet<TFilters['meta'], TFilters['key'], TPackage>;

    abstract getItineraryPackageLoaderConfigs(
        params: ItineraryParams
    ): ItineraryPackageLoaderConfig[];

    init(params: ItineraryParams) {
        const configs = this.getItineraryPackageLoaderConfigs(params);
        this.itineraryPackageSets = configs.map((config, index) => {
            const packageSet = this.itineraryPackageSets[index];
            if (packageSet) {
                packageSet.reinitialize(config, this.getInitialFilters());
                return packageSet;
            } else {
                return this.getPackageSet(config);
            }
        });
    }

    get expectedPackagesToBeSelected(): number {
        return 1;
    }

    /**
     * set last load time of an itinerary packageSet(base on index)
     *
     * @param {number} index
     * @param date
     * @memberof ItineraryModule
     */
    resetLastLoadTime({ index }: { index: number }) {
        const itineraryPackageSet = this.itineraryPackageSets[index];
        if (itineraryPackageSet) {
            itineraryPackageSet.resetLastLoad();
        }
    }

    /**
     * Call before select a package for do something base on module
     *
     * @abstract
     * @param {({
     *      package: ItineraryPackage | null;
     *      index?: number;
     * })} _params
     * @memberof ItineraryModule
     */
    beforeSelectPackage(_params: { package: ItineraryPackage; index: number }) {
        // empty body for parent class
        // may be overriden by child classes if needed.
    }

    /**
     * load available itinerary packages
     *
     * @param {{ index: number }} params
     * @returns {Promise<any>}
     * @memberof ItineraryModule
     */
    async loadAvailable(params: {
        index: number;
        inForm?: boolean;
    }): Promise<void> {
        const packageSet = this.itineraryPackageSets[params.index];
        if (!packageSet) {
            return;
        }
        // restore before load states
        this.listErrorReason = null;
        this.lateralPackage = null;

        const itineraryStore = useItineraryStore();
        try {
            // Loading packages
            await packageSet.loadAvailable();
            this.sortBy = this.defaultSortBy;
            this.isInitialSort = true;
            this.lateralPackage = null;
            if (
                packageSet.list.every(item => !item.reservable) &&
                !params.inForm
            ) {
                await this.loadLaterals();
            }
        } catch (ex) {
            reportException(ex);
            if (ex instanceof NetworkError) {
                this.listErrorReason = {
                    id: 'network-error',
                    type: 'Network',
                    image: '',
                    message: 'network error',
                    description: 'unknown network error',
                };
            }
            if (ex instanceof ServerDisconnectToDateException) {
                this.listErrorReason = {
                    id: 'error-timeout',
                    type: 'Timeout',
                    image: this.unknownListError.image,
                    message: ex.message,
                    description: 'دقایقی دیگر منتظرتان هستیم.',
                    timeout: dayConfig
                        .gregory(ex.disconnectToDate)
                        .add(1, 'minute')
                        .toDate(),
                };
            }
            if (ex instanceof TrainNoServerResponseError) {
                this.listErrorReason = {
                    image: this.unknownListError.image,
                    message: ex.message,
                    type: 'Unknown',
                    id: 'server-no-response',
                    description: '',
                };
            }
        } finally {
            // Loading prices for the next month
            const currentSearchForm = useCurrentSearchForm();
            if (!params.inForm) {
                currentSearchForm.value.loadPrices();
            }
            // Load alternative suggestions when we just loaded the main reserve's results
            if (!packageSet.isSuggestion) {
                itineraryStore.loadSuggestions();
            }
        }
    }

    /**
     * load available laterals
     *
     * @memberof ItineraryModule
     */
    abstract loadLaterals(): Promise<void>;

    /**
     * GETTER - Indicates whether the specified set is loaded, or not
     *
     * @memberof ItineraryModule
     */
    isSetLoaded(setIndex: number) {
        const itineraryPackageSet = this.itineraryPackageSets[setIndex];
        return itineraryPackageSet?.isLoaded;
    }

    get hasItinerarySets(): boolean {
        return this.itineraryPackageSets.some(p => !!p);
    }

    /**
     * GETTER - Indicates whether all the packages in current page is full or not
     *
     * @memberof ItineraryModule
     */
    get areAllPackagesFull() {
        return !this.reservableListWithoutLaterals.length;
    }

    /**
     * GETTER - Returns the reservable sorted list of itinerary packages without laterals
     *
     * @memberof ItineraryModule
     */
    get reservableListWithoutLaterals() {
        return this.sortedReservableList.filter(
            p => p.reserveType === this.reserveInfo.type
        );
    }

    /**
     * current itinerary have any tracking or not
     *
     * @readonly
     * @abstract
     * @type {boolean}
     * @memberof ItineraryModule
     */
    abstract get haveTracking(): boolean;

    /**
     * parse path and do reserve process base on query strings
     *
     * @abstract
     * @param {string} path
     * @return {*}  {Promise<void>}
     * @memberof ItineraryModule
     */
    abstract formAutoReserve(path: LocationQuery): Promise<void>;

    /**
     * get list of filters
     *
     * @readonly
     * @abstract
     * @type {Filter[]}
     * @memberof ItineraryModule
     */
    abstract get filters(): Filter[];

    get filtersLoading() {
        return !this.currentAllPackagesLoaded;
    }

    sortFilters(filters: Filter[]) {
        const prevCounts = process.client
            ? (JSON.parse(localStorage.getItem('filters') ?? '{}') as Record<
                  string,
                  number
              >)
            : {};
        return filters
            .map(filter => ({
                ...filter,
                usage: prevCounts[filter.name] || 0,
            }))
            .sort((a, b) => b.usage - a.usage);
    }

    /**
     * GETTER - Returns the list of promoted itinerary packages, to be shown in a special section
     *
     * @memberof ItineraryModule
     */
    get promotedList() {
        if (!this.currentItineraryPackages) {
            return [];
        }
        return this.list.filter((pkg: ItineraryPackage) => pkg.isPromoted);
    }

    /**
     * GETTER - The count of itinerary packages, before filters being applied
     *
     * @memberof ItineraryModule
     */
    get unfilteredCount() {
        if (!this.currentItineraryPackages) {
            return 0;
        }
        return this.currentItineraryPackages.length;
    }

    /**
     * GETTER - Returns the list of available ItineraryPackage packages
     *
     * @memberof ItineraryModule
     */
    get list(): ItineraryPackage[] {
        if (!this.currentItineraryPackages || !this.currentPackageSet) {
            return [];
        }
        return this.currentItineraryPackages.filter(pkg =>
            this.applyFilter(pkg, this.currentPackageSet!.filters)
        );
    }

    get srpImageAlt(): string | null {
        return `رزرو ${this.reserveInfo.title}`;
    }

    /**
     * UTILITY - When overridden, applies the values of filter to a itinerary package, and return true if the package satisfies the filters
     *
     * @abstract
     * @param {ItineraryPackage} pkg
     * @param {{ [key: string]: any }} filters
     * @returns {boolean}
     * @memberof ItineraryModule
     */
    abstract applyFilter(
        pkg: ItineraryPackage,
        filters: Record<string, any>
    ): boolean;

    /**
     * GETTER - Returns the itinerary packages from the current set
     */
    abstract get currentItineraryPackages(): ItineraryPackage[] | null;

    /**
     * get current loading status
     *
     * @readonly
     * @memberof ItineraryModule
     */
    get currentLoadingStatus() {
        return this.currentPackageSet?.loadingStatus;
    }

    get isRealTimeLoading() {
        return (
            this.currentPackageSet?.loadingStatus ===
            LoadingStatus.RealtimeLoading
        );
    }

    /**
     * get current all package loaded or not
     *
     * @readonly
     * @memberof ItineraryModule
     */
    get currentAllPackagesLoaded() {
        return (
            this.currentPackageSet?.loadingStatus !==
            LoadingStatus.RealtimeLoading
        );
    }

    /**
     * get current package set
     *
     * @readonly
     * @abstract
     * @type {(ItineraryPackageSet<TFilterMetaData, TFilterKey> | null)}
     * @memberof ItineraryModule
     */
    abstract get currentPackageSet():
        | ItineraryPackageSet<TFilters['meta'], TFilters['key']>
        | null
        | undefined;

    /**
     * get the title of current itinerary
     *
     * @readonly
     * @abstract
     * @type {string}
     * @memberof ItineraryModule
     */
    abstract get mainTitle(): string;

    abstract get dateTitle(): string;

    abstract get alternativeSuggestionTypes(): [ReserveType, ReserveType];

    static revivePackage(packageLike: ItineraryPackage) {
        let pack: ItineraryPackage | undefined;
        switch (packageLike.concreteType) {
            case ReserveType.Flight:
                pack = new FlightPackage();
                break;
            case ReserveType.Train:
                pack = new TrainPackage();
                break;
            case ReserveType.Hotel:
                pack = new HotelPackage();
                break;
            case ReserveType.Bus:
                pack = new BusPackage();
                break;
        }
        if (!pack) {
            return packageLike;
        }
        Object.assign(pack, packageLike);
        if (packageLike.selectedPrices?.length) {
            pack.selectedPrices = [];
            packageLike.selectedPrices.forEach(price => {
                let priceClass: PriceClass | null = null;
                switch (packageLike.concreteType) {
                    case ReserveType.Flight:
                        priceClass = pack?.autoReserveEnabled
                            ? new FlightAutoPriceClass()
                            : new FlightPriceClass();
                        break;
                    case ReserveType.Train:
                        priceClass = new TrainPriceClass();
                        break;
                    case ReserveType.Bus:
                        priceClass = pack?.autoReserveEnabled
                            ? new BusAutoPriceClass()
                            : new BusPriceClass();
                        break;
                    case ReserveType.Hotel:
                        priceClass = new HotelPriceClass(
                            (price as HotelPriceClass).room,
                            (price as HotelPriceClass).options
                        );
                        break;
                }
                if (!priceClass) {
                    return;
                }
                Object.assign(priceClass, price);
                pack.selectedPrices!.push(priceClass);
            });
        }
        if (pack.isTripPackage()) {
            pack.segments[0].trips[0].departureDateTime = new Date(
                pack.segments[0].trips[0].departureDateTime
            );
            if (pack.isFlightPackage()) {
                pack.segments = [...new Array(pack.segments.length)]
                    .map(() => new FlightSegment([], [], ''))
                    .map((seg, index) =>
                        Object.assign(seg, pack.segments[index])
                    );
            } else {
                (pack as any).segments = [...new Array(pack.segments.length)]
                    .map(() => new TripSegment([], [], ''))
                    .map((seg, index) =>
                        Object.assign(
                            seg,
                            (pack as FlightPackage).segments[index]
                        )
                    );
            }
        }
        if (pack.isHotelPackage()) {
            pack.roomsPackageList = pack.roomsPackageList.map(
                p =>
                    new RoomsPackage({
                        rooms: (p as any)._roomsList.map((r: any) => {
                            const rt = new HotelRoomType({} as any);
                            Object.assign(rt, r);
                            return rt;
                        }),
                    })
            );
        }
        return pack;
    }

    toJSON() {
        return { ...this };
    }
}
